import { useSiteData, usePageContent } from '@/context/SiteDataContext';
import EditText from '@/components/editable/EditText';
import EditImage from '@/components/editable/EditImage';
import FaithItems from '@/components/editable/FaithItems';
import SectionToggle, { useSectionHidden } from '@/components/editable/SectionToggle';
import GalleryRows from '@/components/GalleryRows';

export default function About() {
  const { timeline } = useSiteData();
  const c = usePageContent('about');
  const timelineList = timeline || [];
  const hideHistory = useSectionHidden('about', 'history');
  const hideMvv = useSectionHidden('about', 'mvv');
  const hideFaith = useSectionHidden('about', 'faith');
  const hideTimeline = useSectionHidden('about', 'timeline');
  return (
    <div>
      <section className="relative py-32 bg-royal-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          {c.hero_video_url ? (
            <video autoPlay muted loop playsInline data-testid="about-hero-video" className="w-full h-full object-cover">
              <source src={c.hero_video_url} type="video/mp4" />
            </video>
          ) : (
            <img src={c.hero_image || 'https://images.unsplash.com/photo-1619899783752-a678a3a035b5?w=2400'} alt="Heritage" className="w-full h-full object-cover opacity-30" />
          )}
          <div className="absolute inset-0 bg-royal-dark/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <EditText field="hero_overline" target="page:about" as="div" className="overline text-gold mb-4">{c.hero_overline || 'About Us'}</EditText>
          <EditText field="hero_heading" target="page:about" as="h1" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{c.hero_heading || 'A Rich Heritage. A Living Mission.'}</EditText>
          <EditText field="hero_text" target="page:about" as="p" multiline className="mt-6 text-white/80 text-lg">{c.hero_text || 'Rooted in the historic Church of God in Christ, the Wisconsin First Jurisdiction serves the state as a beacon of Kingdom truth and Spirit-empowered ministry.'}</EditText>
        </div>
      </section>

      {!hideHistory && (
      <section className="bg-white py-20 lg:py-28 relative">
        <div className="absolute top-4 right-4"><SectionToggle pageSlug="about" sectionKey="history" label="History" /></div>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          <div>
            <EditText field="history_overline" target="page:about" as="div" className="overline mb-3">{c.history_overline || 'Our History'}</EditText>
            <EditText field="history_heading" target="page:about" as="h2" className="font-serif text-3xl lg:text-4xl text-royal font-bold mb-6 gold-underline">{c.history_heading || 'Built on the Solid Rock'}</EditText>
            <EditText field="history_text" target="page:about" as="p" multiline className="text-charcoal-muted text-lg leading-relaxed whitespace-pre-line">{c.history_text || 'Since our chartering in 1978, the Wisconsin First Ecclesiastical Jurisdiction has served as a bastion of holiness, biblical preaching, and Spirit-led worship throughout Wisconsin. From Milwaukee to Madison, Racine to Sheboygan, our churches carry the enduring witness of the historic Church of God in Christ movement founded by Bishop Charles Harrison Mason in 1907.'}</EditText>
          </div>
          <div>
            <EditImage field="about_history_image" target="page:about" category="pages" accept="image/*">
              <img src={c.about_history_image || 'https://images.unsplash.com/photo-1519452575417-564c1401ecc0?w=1200'} alt="History" className="rounded-xl shadow-lg w-full h-full object-cover max-h-[520px]" />
            </EditImage>
          </div>
        </div>
      </section>)}

      {!hideMvv && (
      <section className="bg-[#F8F9FA] py-20 lg:py-28 relative">
        <div className="absolute top-4 right-4"><SectionToggle pageSlug="about" sectionKey="mvv" label="Mission·Vision·Values" /></div>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          {[
            { key: 'mvv_mission', title: 'Mission', text: 'To glorify God through anointed worship, uncompromising preaching, disciple-making, and Spirit-empowered service to Wisconsin and the world.' },
            { key: 'mvv_vision', title: 'Vision', text: 'To be a Kingdom-advancing jurisdiction where every believer is equipped, every church is thriving, and every community is transformed.' },
            { key: 'mvv_values', title: 'Core Values', text: 'Holiness · Excellence · Unity · Servanthood · Generational Discipleship · Kingdom Impact.' },
          ].map((b) => (
            <div key={b.key} className="card-tactile p-8">
              <EditText field={`${b.key}_title`} target="page:about" as="h3" className="font-serif text-2xl text-royal font-bold mb-4">{c[`${b.key}_title`] || b.title}</EditText>
              <EditText field={b.key} target="page:about" as="p" multiline className="text-charcoal-muted leading-relaxed">{c[b.key] || b.text}</EditText>
            </div>
          ))}
        </div>
      </section>)}

      {!hideFaith && (
      <section className="bg-white py-20 lg:py-28 relative">
        <div className="absolute top-4 right-4"><SectionToggle pageSlug="about" sectionKey="faith" label="Statement of Faith" /></div>
        <div className="max-w-4xl mx-auto px-6">
          <EditText field="faith_overline" target="page:about" as="div" className="overline mb-3">{c.faith_overline || 'What We Believe'}</EditText>
          <EditText field="faith_heading" target="page:about" as="h2" className="font-serif text-3xl lg:text-4xl text-royal font-bold mb-8 gold-underline">{c.faith_heading || 'Statement of Faith'}</EditText>
          <FaithItems items={c.faith_items} />
        </div>
      </section>)}

      {!hideTimeline && (
      <section className="bg-[#F8F9FA] py-20 lg:py-28 relative">
        <div className="absolute top-4 right-4"><SectionToggle pageSlug="about" sectionKey="timeline" label="Timeline" /></div>
        <div className="max-w-5xl mx-auto px-6">
          <EditText field="timeline_overline" target="page:about" as="div" className="overline mb-3">{c.timeline_overline || 'Milestones'}</EditText>
          <EditText field="timeline_heading" target="page:about" as="h2" className="font-serif text-3xl lg:text-4xl text-royal font-bold mb-14 gold-underline">{c.timeline_heading || 'Jurisdiction Timeline'}</EditText>
          <div className="relative border-l-2 border-gold/40 space-y-10">
            {timelineList.map((t) => (
              <div key={t.year} className="relative pl-8 md:pl-12">
                <div className="absolute left-0 -ml-[9px] w-4 h-4 rounded-full bg-gold border-4 border-white ring-2 ring-gold" />
                <div className="font-serif text-royal text-2xl font-bold">{t.year} · {t.title}</div>
                <div className="text-charcoal-muted mt-2">{t.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>)}

      {/* Universal photo blocks for the About page bottom */}
      <section className="bg-white py-14" data-testid="about-photo-blocks">
        <div className="max-w-6xl mx-auto px-6">
          <GalleryRows scope="page:about-bottom" empty="" />
        </div>
      </section>
    </div>
  );
}
